import Image from 'next/image';
import { logger } from '@/utils/logger';
import { twMerge } from 'tailwind-merge';
import { Loading, Alert } from '@zopauk/web-ui';
import { TextLink } from '@/components/TextLink';
import { truncateText } from '@/utils/truncateText';
import { ButtonLink } from '@/components/ButtonLink';
import { formatDistanceToNowStrict } from 'date-fns';
import Slider, { type CustomArrowProps } from 'react-slick';
import type { TrustpilotFragment } from '@/utils/dato/generated';
import { useBreakpointValue } from '@/utils/hooks/useBreakpointValue';
import { useTrustpilotReviews } from '@/utils/fetchers/trustpilot/reviews';
import { useTrustpilotSummary } from '@/utils/fetchers/trustpilot/summary';

const REVIEW_MAX_LENGTH = 250;

const NextArrow = ({ className, onClick }: CustomArrowProps) => (
  <button
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 right-[-10px] cursor-pointer md:p-[10px] bg-transparent z-10',
      className,
    )}
    onClick={onClick}
  >
    <span className="material-symbols-outlined text-7xl!">chevron_right</span>
    <span className="sr-only">Next Slide</span>
  </button>
);

const PrevArrow = ({ className, onClick }: CustomArrowProps) => (
  <button
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 left-[-10px] cursor-pointer md:p-[10px] bg-transparent z-10',
      className,
    )}
    onClick={onClick}
  >
    <span className="material-symbols-outlined text-7xl!">chevron_left</span>
    <span className="sr-only">Previous Slide</span>
  </button>
);

const SLIDES_TO_SHOW_BREAKPOINTS = [
  { maxWidth: 768, value: 1 },
  { maxWidth: 992, value: 2 },
];

const Trustpilot = ({ entries }: TrustpilotFragment) => {
  const reviewsQuery = useTrustpilotReviews();
  const summaryQuery = useTrustpilotSummary();
  const slidesToShow = useBreakpointValue(SLIDES_TO_SHOW_BREAKPOINTS, 3);

  if (reviewsQuery?.data && summaryQuery?.data) {
    const reviews = reviewsQuery.data;
    const summary = summaryQuery.data;

    return (
      <>
        {slidesToShow !== undefined ? (
          <Slider
            className="px-8 select-auto mb-8 mt-11 [&_.slick-list]:select-auto md:[&_.slick-list]:mx-[-32px] [&_.slick-slide]:px-7"
            slidesToScroll={slidesToShow}
            slidesToShow={slidesToShow}
            nextArrow={<NextArrow />}
            prevArrow={<PrevArrow />}
            lazyLoad="progressive"
            draggable={false}
            infinite={true}
            dots={false}
            speed={500}
          >
            {reviews.reviews.slice(0, entries || 6).map((review) => (
              <div className="text-center md:text-left" key={review.id}>
                <Image
                  src={`https://images-static.trustpilot.com/api/stars/${Math.round(review.stars * 2) / 2}/128x24.png`}
                  alt={`${review.stars} stars from ${review.consumer.displayName} in trustpilot`}
                  className="mb-2 mx-auto md:mx-0"
                  height="20"
                  width="105"
                />
                <span className="block font-semibold mb-2">{review.title}</span>
                <p className="mb-2">{truncateText(review.text, REVIEW_MAX_LENGTH)}</p>

                {review.text.length > REVIEW_MAX_LENGTH ? (
                  <TextLink
                    href={`https://uk.trustpilot.com/reviews/${review.id}`}
                    rel="noopener nofollow"
                    target="_blank"
                  >
                    Read more
                  </TextLink>
                ) : null}

                <div className="pt-6 flex justify-between items-start gap-2">
                  <span className="font-semibold">{review.consumer.displayName}</span>
                  <p className="pt-1 text-sm shrink-0">{formatDistanceToNowStrict(new Date(review.createdAt))} ago</p>
                </div>
              </div>
            ))}
          </Slider>
        ) : null}
        <div className="text-center">
          <div>
            <Image
              src="https://images-static.trustpilot.com/api/logos/light-bg/logo.svg"
              className="mx-auto mb-3"
              alt="Trustpilot logo"
              height="50"
              width="160"
            />
          </div>
          <Image
            src={`https://images-static.trustpilot.com/api/stars/${Math.round(summary.score.stars * 2) / 2}/512x96.png`}
            alt={`Average star score of ${summary.score.stars} in trustpilot`}
            className="mx-auto mb-3"
            height="28"
            width="160"
          />
          <p className="mb-8">Based on {summary.numberOfReviews.total} reviews</p>

          <ButtonLink
            href="https://uk.trustpilot.com/review/zopa.com"
            data-automation="ZA.button-Read-more-reviews"
            rel="noopener noreferrer"
            variant="secondary"
            className="mb-6"
            target="_blank"
            outline
          >
            Read more reviews
          </ButtonLink>
        </div>
      </>
    );
  }

  if (summaryQuery.error || reviewsQuery.error) {
    logger.error({
      message: '[Trustpilot] - Unable to get data from trustpilot',
      error: summaryQuery.error || reviewsQuery.error,
    });
    return <Alert variant="warning">Unable to retrieve reviews, please try again later.</Alert>;
  }

  return <Loading variant="spinner" />;
};

export default Trustpilot;
